import styled from "styled-components";
import { useEffect, useState } from "react";
import { addDays, format, isToday, startOfWeek } from "date-fns";
import { useGetWeekMenu } from "./useGetWeekMenu";
import { fetchLunchSnapshotById } from "./useGetLunchSnapshotById";

const CardContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 1.2rem;
  border-radius: 1rem;
  width: 100%;
  height: 100%;
  overflow-y: auto;
`;

const MealTextContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const Header = styled.h5`
  color: var(--color-grey-500);
  padding: 1rem;
`;

const DayRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1.6rem;
  margin-bottom: 0.8rem;
  padding: 0.4rem 0.8rem;

  border-radius: 0.6rem;
`;

const DayLabel = styled.div`
  flex: 1;
  font-size: ${({ $highlight }) => ($highlight ? "1.2rem" : "1rem")};
  font-weight: ${({ $highlight }) => ($highlight ? "600" : "400")};
  color: ${({ $highlight }) =>
    $highlight ? "var(--color-witron-blue)" : "var(--color-grey-500)"};
`;

const MealLabel = styled.div`
  flex: 1;
  font-size: ${({ $highlight }) => ($highlight ? "1.2rem" : "1rem")};
  font-weight: ${({ $highlight }) => ($highlight ? "1000" : "800")};
  font-size: ${({ $highlight }) => ($highlight ? "1.5em" : "1.2em")};
  color: ${({ $highlight }) =>
    $highlight ? "var(--color-witron-blue)" : "var(--color-grey-500)"};
`;

const Snapshot = styled.img`
  width: ${({ $highlight }) => ($highlight ? "150px" : "50px")};
  height: ${({ $highlight }) => ($highlight ? "150px" : "50px")};
  object-fit: cover;
  border-radius: 2.8rem;
  box-shadow: var(--shadow-div-sm);
  background-color: #2c2f33;
`;

const daysOfWeek = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

const WeekMenuPreview = () => {
  const [currentWeekStart] = useState(
    startOfWeek(new Date(), { weekStartsOn: 1 })
  );
  const { data: storedWeekMenu } = useGetWeekMenu(
    format(currentWeekStart, "yyyy-MM-dd")
  );

  const [snapshots, setSnapshots] = useState({});
  const noMealImage = "/images/NoMealImage.png";

  useEffect(() => {
    const fetchSnapshots = async () => {
      if (!storedWeekMenu?.days?.length) return;

      const mealIds = [
        ...new Set(storedWeekMenu.days.map((d) => d.mealId).filter(Boolean)),
      ];

      const map = {};
      for (const id of mealIds) {
        try {
          const res = await fetchLunchSnapshotById(id);
          map[id] = res || null;
        } catch (e) {
          map[id] = null;
        }
      }

      setSnapshots(map);
    };

    fetchSnapshots();
  }, [storedWeekMenu]);

  const startDate = format(currentWeekStart, "MMM d");
  const endDate = format(addDays(currentWeekStart, 6), "MMM d");

  return (
    <CardContainer>
      <Header>
        Week Menu: {startDate} – {endDate}
      </Header>
      {storedWeekMenu?.days
        ?.filter((day) => {
          const weekday = format(new Date(day.date), "EEEE");
          return (
            ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"].includes(
              weekday
            ) ||
            (["Saturday", "Sunday"].includes(weekday) && !!day.mealName)
          );
        })
        .map((day) => {
          const weekday = format(new Date(day.date), "EEEE");
          const highlight = isToday(new Date(day.date));
          const snapshot = snapshots[day.mealId] || noMealImage;

          return (
            <DayRow key={day.date} $highlight={highlight}>
              <MealTextContainer>
                {" "}
                <DayLabel $highlight={highlight}>{weekday}</DayLabel>
                <MealLabel $highlight={highlight}>
                  {day.mealName || ""}
                </MealLabel>
              </MealTextContainer>

              <Snapshot
                src={snapshot}
                alt={`Meal for ${day.mealName}`}
                $highlight={highlight}
              />
            </DayRow>
          );
        })}
    </CardContainer>
  );
};

export default WeekMenuPreview;
